import { useRef, useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, Building, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Empresa {
  id_empresa: string;
  empresa: string;
  logo: string | null;
  categoria_slug: string;
  ciudad_slug: string;
  empresa_slug: string;
  ciudad?: string | null;
  direccion_completa?: string | null;
  distancia?: number | null;
  valoracion_media_empresa?: number | null;
  total_valoraciones?: number | null;
  ultima_reserva_usuario?: string | null;
  imagenes?: string[];
}

interface Props {
  empresas: Empresa[];
}

const SITE_URL = 'https://bookoll.com';
// En móvil la tarjeta no ocupa el ancho completo a propósito: así asoma la
// siguiente por el borde y se ve que el carrusel continúa.
const CARD_WIDTH = 'w-[80vw] max-w-[420px] sm:w-[420px] sm:max-w-none';
const SCROLL_AMOUNT = 360;

// Tope de tarjetas del carrusel. Con filtros activos un tope bajo escondía
// resultados que sí cumplían el filtro.
const MAX_TARJETAS = 20;

const slugToLabel = (slug: string) =>
  slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const RecommendedCompanies = ({ empresas }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const empresasFiltradas = useMemo(() => {
    if (!empresas?.length) return [];
    return empresas.slice(0, MAX_TARJETAS);
  }, [empresas]);

  // Medida del carrusel: de aquí salen las flechas (solo si desborda y sin
  // llegar al extremo), el degradado del borde y los puntos de paginación.
  // Las flechas siguen siendo solo de PC (hidden sm:flex).
  const [scrollEstado, setScrollEstado] = useState({
    desborda: false,
    inicio: true,
    fin: false,
    indice: 0,
  });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const medir = () => {
      const desborda = el.scrollWidth > el.clientWidth + 1;

      // Tarjeta visible: la que tiene su borde izquierdo más cerca del
      // scroll actual. Se mide sobre el DOM en vez de dividir por un ancho
      // fijo, que con `vw` y el gap nunca cuadra del todo.
      let indice = 0;
      let minima = Infinity;
      Array.from(el.children).forEach((hijo, i) => {
        const distancia = Math.abs((hijo as HTMLElement).offsetLeft - el.offsetLeft - el.scrollLeft);
        if (distancia < minima) {
          minima = distancia;
          indice = i;
        }
      });

      setScrollEstado({
        desborda,
        inicio: el.scrollLeft <= 1,
        fin: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
        indice,
      });
    };

    medir();
    el.addEventListener('scroll', medir, { passive: true });
    const observer = new ResizeObserver(medir);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', medir);
      observer.disconnect();
    };
  }, [empresasFiltradas.length]);

  if (!empresasFiltradas.length) return null;

  const irATarjeta = (i: number) => {
    const el = scrollRef.current;
    const hijo = el?.children[i] as HTMLElement | undefined;
    if (!el || !hijo) return;
    el.scrollTo({ left: hijo.offsetLeft - el.offsetLeft, behavior: 'smooth' });
  };

  const scroll = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  // ── ItemList schema ─────────────────────────────────────────────────────────
  // Google puede mostrar estas empresas como rich results en la SERP
  // cuando alguien busca negocios relacionados con Bookoll.
  // Solo se inyecta cuando hay empresas cargadas.
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Negocios recomendados en Bookoll',
    description:
      'Selección de negocios destacados con reserva online disponible en Bookoll.',
    numberOfItems: empresasFiltradas.length,
    itemListElement: empresasFiltradas.map((e, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: e.empresa,
      url: `${SITE_URL}/${e.categoria_slug}/${e.ciudad_slug}/${e.empresa_slug}`,
    })),
  };

  return (
    <>
      {/* ── ItemList schema inyectado solo cuando hay datos ── */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
      </Helmet>

      <div className="w-full px-4 sm:px-0 pt-4 pb-4 sm:pl-[15%] sm:pr-[15%]">
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory py-2 no-scrollbar"
          >
            {empresasFiltradas.map((empresa, i) => {
              const hasRating = (empresa.valoracion_media_empresa ?? 0) > 0;
              const categoriaLabel = slugToLabel(empresa.categoria_slug);

              return (
                <motion.div
                  key={empresa.id_empresa}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.45, ease: 'easeOut', delay: Math.min(i, 4) * 0.08 }}
                  whileHover={{ y: -6 }}
                  className={`group ${CARD_WIDTH} snap-start relative bg-white dark:bg-[#1c1c1e] rounded-xl border border-gray-100 dark:border-white/10 shadow-sm hover:shadow-xl hover:shadow-[#103254]/10 transition-shadow duration-300 cursor-pointer flex-shrink-0`}
                  onClick={() =>
                    navigate(
                      `/${empresa.categoria_slug}/${empresa.ciudad_slug}/${empresa.empresa_slug}`,
                      { state: { fromClientBooking: true } }
                    )
                  }
                >
                  {/* Imagen: recorta el zoom del hover */}
                  <div className="relative overflow-hidden rounded-t-xl">
                    {empresa.imagenes?.length ? (
                      <img
                        src={empresa.imagenes[0]}
                        alt={`${empresa.empresa} — ${slugToLabel(
                          empresa.categoria_slug
                        )}`}
                        loading="lazy"
                        className="w-full h-[210px] object-cover object-center rounded-t-xl transition-transform duration-500 ease-out group-hover:scale-105"
                      />
                    ) : empresa.logo ? (
                      <div className="w-full h-[210px] flex items-center justify-center bg-gray-50 dark:bg-white/5 rounded-t-xl">
                        <img
                          src={empresa.logo}
                          alt={`Logo de ${empresa.empresa}`}
                          loading="lazy"
                          className="w-[180px] h-[180px] rounded-full object-cover border dark:border-white/10"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-[210px] flex items-center justify-center rounded-t-xl bg-gray-200 dark:bg-white/10">
                        <Building className="w-8 h-8 text-gray-500 dark:text-gray-300" />
                      </div>
                    )}

                    {/* Badge categoría sobre la imagen */}
                    <span className="absolute top-3 left-3 bg-white/90 dark:bg-black/70 backdrop-blur-sm text-[#103254] dark:text-white text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm">
                      {categoriaLabel}
                    </span>
                  </div>

                  <div className="p-4 flex flex-col items-start text-left">
                    <div className="flex items-center gap-1 w-full">
                      <span className="text-lg font-medium text-gray-800 dark:text-white truncate flex-1">
                        {empresa.empresa}
                      </span>

                      {hasRating && (
                        <div className="flex items-center gap-0.5 text-xs font-semibold text-gray-700 dark:text-gray-300 shrink-0">
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          {empresa.valoracion_media_empresa!.toFixed(1)}
                          {!!empresa.total_valoraciones && (
                            <span className="text-gray-400 dark:text-gray-500 font-normal text-[11px]">
                              ({empresa.total_valoraciones})
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {(empresa.direccion_completa ?? empresa.ciudad) && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                        {empresa.direccion_completa ?? empresa.ciudad}
                      </span>
                    )}

                    {empresa.distancia != null && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('recommendedCompanies.distance', {
                          km: empresa.distancia.toFixed(1),
                        })}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* El degradado corta la tarjeta que asoma, en vez de dejarla
              terminada en seco: da a entender que el contenido sigue.
              Desaparece al llegar al final. */}
          {scrollEstado.desborda && !scrollEstado.fin && (
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 sm:w-16 bg-gradient-to-l from-white dark:from-black to-transparent" />
          )}

          {/* Las flechas no se desmontan al llegar a un extremo: si
              desaparecieran, el siguiente clic caería en la tarjeta de debajo
              y abriría la empresa. Se quedan desactivadas y atenuadas. */}
          {scrollEstado.desborda && (
            <>
              <Button
                size="icon"
                variant="outline"
                onClick={() => scroll('left')}
                disabled={scrollEstado.inicio}
                aria-label="Anterior"
                className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white dark:bg-[#1c1c1e] dark:text-white dark:border-white/10 shadow w-10 h-10 disabled:opacity-40 disabled:pointer-events-auto disabled:cursor-default"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => scroll('right')}
                disabled={scrollEstado.fin}
                aria-label="Siguiente"
                className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white dark:bg-[#1c1c1e] dark:text-white dark:border-white/10 shadow w-10 h-10 disabled:opacity-40 disabled:pointer-events-auto disabled:cursor-default"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </>
          )}
        </div>

        {/* Puntos de paginación: la pista más reconocible de que hay más.
            Con muchas tarjetas se amontonan, así que a partir de OCHO se
            cambian por un contador. */}
        {scrollEstado.desborda && (
          <div className="flex justify-center items-center gap-1.5 pt-3">
            {empresasFiltradas.length <= 8 ? (
              empresasFiltradas.map((empresa, i) => (
                <button
                  key={empresa.id_empresa}
                  type="button"
                  onClick={() => irATarjeta(i)}
                  aria-label={`${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === scrollEstado.indice
                      ? 'w-5 bg-[#288E7F]'
                      : 'w-1.5 bg-gray-300 dark:bg-white/25'
                  }`}
                />
              ))
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {scrollEstado.indice + 1} / {empresasFiltradas.length}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default RecommendedCompanies;